import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import PatientHeartRateRepository from '../clients/db/PatientHeartRateRepository'
import PatientGlucoseRateRepository from '../clients/db/PatientGlucoseRateRepository'
import { AppThunk } from '../store'
import PatientHeartRate from 'model/PatientHeartRate'
import PatientSpo2 from 'model/PatientSpo2'
import PatientSpo2Repository from 'clients/db/PatientSpo2Repository'
import PatientTemperature from 'model/PatientTemperature'
import PatientTemperatureRepository from 'clients/db/PatientTemperatureRepository'
import PatientActivity from 'model/PatientActivity'
import PatientActivityRepository from 'clients/db/PatientActivityRepository'
import PatientBloodPressureRepository from 'clients/db/PatientBloodPressureRepository'
import PatientBloodPressure from 'model/PatientBloodPressure'
import PatientGlucoseRate from 'model/PatientGlucoseRate'
import PatientWeightRepository from 'clients/db/PatientWeightRepository'

interface PatientGarminState {
  isLoading: boolean
  isUpdatedSuccessfully: boolean
  patientHeartRate: PatientHeartRate[]
  patientGlucoseRate: PatientGlucoseRate[]
  patientSpo2: PatientSpo2[]
  patientTemperature: PatientTemperature[]
  patientWeight: PatientSpo2[]
  patientActivity: PatientActivity[]
  patientBloodPressure: PatientBloodPressure[]
}

const initialState: PatientGarminState = {
  isLoading: false,
  isUpdatedSuccessfully: false,
  patientHeartRate: [] as PatientHeartRate[],
  patientGlucoseRate: [] as PatientGlucoseRate[],
  patientSpo2: [] as PatientSpo2[],
  patientWeight: [] as PatientSpo2[],
  patientTemperature: [] as PatientTemperature[],
  patientActivity: [] as PatientActivity[],
  patientBloodPressure: [] as PatientBloodPressure[],
}

function startLoading(state: PatientGarminState) {
  state.isLoading = true
}

const patientGarminSlice = createSlice({
  name: 'patientMonitoring',
  initialState,
  reducers: {
    fetchPatientGarminStart: startLoading,
    fetchPatientHeartRateSuccess(state, { payload }: PayloadAction<PatientHeartRate[]>) {
      state.isLoading = false
      state.patientHeartRate = payload
    },
    fetchPatientGlucoseRateSuccess(state, { payload }: PayloadAction<PatientGlucoseRate[]>) {
      state.isLoading = false
      state.patientGlucoseRate = payload
    },
    fetchPatientBloodPressureSuccess(state, { payload }: PayloadAction<PatientBloodPressure[]>) {
      state.isLoading = false
      state.patientBloodPressure = payload
    },
    fetchPatientSpo2Success(state, { payload }: PayloadAction<PatientSpo2[]>) {
      state.isLoading = false
      state.patientSpo2 = payload
    },
    fetchPatientWeightSuccess(state, { payload }: PayloadAction<PatientSpo2[]>) {
      state.isLoading = false
      state.patientWeight = payload
    },
    fetchPatientTemperatureSuccess(state, { payload }: PayloadAction<PatientTemperature[]>) {
      state.isLoading = false
      state.patientTemperature = payload
    },
    fetchPatientActivitySuccess(state, { payload }: PayloadAction<PatientActivity[]>) {
      state.isLoading = false
      state.patientActivity = payload
    },
  },
})

export const {
  fetchPatientGarminStart,
  fetchPatientHeartRateSuccess,
  fetchPatientGlucoseRateSuccess,
  fetchPatientBloodPressureSuccess,
  fetchPatientSpo2Success,
  fetchPatientWeightSuccess,
  fetchPatientTemperatureSuccess,
  fetchPatientActivitySuccess,
} = patientGarminSlice.actions

export const fetchPatientGarminHeart = (
  id: string,
  startDate: moment.Moment | undefined,
  endDate: moment.Moment | undefined,
  justLastValue: boolean,
): AppThunk => async dispatch => {
  dispatch(fetchPatientGarminStart())

  let patientHeartRate

  if (justLastValue) {
    patientHeartRate = await PatientHeartRateRepository.getLastValue(id)
  } else {
    patientHeartRate = await PatientHeartRateRepository.searchByDate(id, startDate, endDate)
  }

  if (patientHeartRate) {
    dispatch(fetchPatientHeartRateSuccess(patientHeartRate))
  }
}

export const fetchPatientGarminGlucose = (
  id: string,
  startDate: moment.Moment | undefined,
  endDate: moment.Moment | undefined,
): AppThunk => async dispatch => {
  dispatch(fetchPatientGarminStart())

  const patientHeartRate = await PatientGlucoseRateRepository.searchByDate(id, startDate, endDate)
  if (patientHeartRate) {
    dispatch(fetchPatientGlucoseRateSuccess(patientHeartRate))
  }
}

export const fetchPatientGarminSpo2 = (
  id: string,
  startDate: moment.Moment | undefined,
  endDate: moment.Moment | undefined,
  justLastValue: boolean,
): AppThunk => async dispatch => {
  dispatch(fetchPatientGarminStart())

  let patientSpo2
  if (justLastValue) {
    patientSpo2 = await PatientSpo2Repository.getLastValue(id)
  } else {
    patientSpo2 = await PatientSpo2Repository.searchByDate(id, startDate, endDate)
  }

  if (patientSpo2) {
    dispatch(fetchPatientSpo2Success(patientSpo2))
  }
}

export const fetchPatientGarminWeight = (
  id: string,
  startDate: moment.Moment | undefined,
  endDate: moment.Moment | undefined,
  justLastValue: boolean,
): AppThunk => async dispatch => {
  dispatch(fetchPatientGarminStart())

  let patientSpo2

  if (justLastValue) {
    patientSpo2 = await PatientWeightRepository.getLastValue(id)
  } else {
    patientSpo2 = await PatientWeightRepository.searchByDate(id, startDate, endDate)
  }

  if (patientSpo2) {
    dispatch(fetchPatientWeightSuccess(patientSpo2))
  }
}

export const fetchPatientGarminTemperature = (
  id: string,
  startDate: moment.Moment | undefined,
  endDate: moment.Moment | undefined,
  justLastValue: boolean,
): AppThunk => async dispatch => {
  dispatch(fetchPatientGarminStart())

  let patientTemperature
  if (justLastValue) {
    patientTemperature = await PatientTemperatureRepository.getLastValue(id)
  } else {
    patientTemperature = await PatientTemperatureRepository.searchByDate(id, startDate, endDate)
  }

  if (patientTemperature) {
    dispatch(fetchPatientTemperatureSuccess(patientTemperature))
  }
}

export const fetchPatientGarminBloodPressure = (
  id: string,
  startDate: moment.Moment | undefined,
  endDate: moment.Moment | undefined,
  justLastValue: boolean,
): AppThunk => async dispatch => {
  dispatch(fetchPatientGarminStart())

  let PatientBloodPressure
  if (justLastValue) {
    PatientBloodPressure = await PatientBloodPressureRepository.getLastValue(id)
  } else {
    PatientBloodPressure = await PatientBloodPressureRepository.searchByDate(id, startDate, endDate)
  }
  if (PatientBloodPressure) {
    dispatch(fetchPatientBloodPressureSuccess(PatientBloodPressure))
  }
}

export const fetchPatientGarminActivity = (
  id: string,
  startDate: moment.Moment | undefined,
  endDate: moment.Moment | undefined,
): AppThunk => async dispatch => {
  dispatch(fetchPatientGarminStart())

  const patientActivity = await PatientActivityRepository.searchByDate(id, startDate, endDate)
  if (patientActivity) {
    dispatch(fetchPatientActivitySuccess(patientActivity))
  }
}

export default patientGarminSlice.reducer
